/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable max-lines */
import {
  Box,
  CircularProgress,
  Grid,
  Button as MuiButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import * as moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, Noop, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BuildingsAutocompleteSelectField } from 'src/components/BuildingsAutocompleteField/BuildingsAutocompleteField.container';
import CompanyDropdown from 'src/components/CompanyDropdown/CompanyDropdown';
import { DatePicker } from 'src/components/FormComponents/Components/DatePicker';
import GooglePlaceInput from 'src/components/GooglePlaceInput/GooglePlaceInput';
import { TermsModal } from 'src/components/Terms/TermsModal';
import ApplicationSchema from 'src/configs/APISchemas/Application.schema';
import {
  VALIDATE_APPLICATION_POLICY,
  ValidateApplicationPolicyResponse,
  ValidateApplicationPolicySuccessResponse,
} from 'src/configs/APISchemas/ValidateUserPolicyGraphQL';
import { COMPLIANCE_ERROR_TYPES } from 'src/constants/compliance';
import { ROUTES } from 'src/constants/routes';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { useUnitField } from 'src/helpers/hooks/UnitField/useUnitField';
import {
  COMPLIANCE_SHOW_UNIT_AUTOCOMPLETE_KEY,
  useUnitFieldVisibility,
} from 'src/helpers/hooks/UnitField/useUnitFieldVisibility';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import { useMount } from 'src/helpers/hooks/useMount';
import {
  loadComplianceShowAllBuildingsFieldFromStorage,
  removeComplianceShowAllBuildingsFieldFromStorage,
  saveComplianceShowAllBuildingsFieldToStorage,
} from 'src/storage/all-buildings-autocomplete.storage';
import { Building } from 'src/types';
import Container from '../../components/Container';
import HelperText from '../../components/HelperText/HelperText';
import { NAME_FIELD_REGEX } from '../../constants/constants';
import { STATES } from '../../constants/states';
import {
  defaultUnit,
  useUnitList,
} from '../../helpers/hooks/UnitField/useUnitList';
import useDialog from '../../helpers/hooks/useDialog';
import { useTrackEventForForm } from '../../helpers/useTrackEventForForm';
import { getBuildingsAction } from '../../redux/actions/getBuildings';
import { updateCompanyVendor } from '../../redux/actions/shared';
import {
  resetVerifyPolicy,
  updateComplianceProviderAction,
  updateVerifyPolicyFlow,
  updateVerifyPolicyInput,
} from '../../redux/actions/verifyPolicy';
import { addressDefaultValue } from '../../redux/reducer/v2/purchasePolicy';
import { VerifyPolicyInput } from '../../redux/reducer/v2/verifyPolicy';
import { getPurchasePolicyAdditionalData } from '../../redux/selectors/purchasePolicy';
import { getCompanyVendor } from '../../redux/selectors/shared';
import { getUtmSource } from '../../redux/selectors/system';
import {
  getVerifyPolicyComplianceProvider,
  getVerifyPolicyFlow,
  getVerifyPolicyInput,
} from '../../redux/selectors/verifyPolicy';
import { Loader } from '../StartApplication/StartApplication.loader';
import { ComplianceFormFieldLegend } from './components/ComplianceFormFieldLegend';
import { ComplianceFormHelper } from './components/ComplianceFormHelper';
import { ComplianceFormSectionHeader } from './components/ComplianceFormSectionHeader';
import { ComplianceFormTermsText } from './components/ComplianceFormTermsText';
import UnitAutocompleteField from './components/Fields/UnitAutocompleteField';
import UnitField from './components/Fields/UnitField';
import { validateApplicationPolicySerializer } from './serializers/validate-application-policy.serializer';
import { StyledDropdown } from './styles/ComplianceForm.styles';
import { checkDatesValidity } from './utils/check-dates-validity.util';
import { handleApplicationStatus, handleError } from './utils/compliance.util';
import { getResponseComplianceLink } from './utils/get-response-compliance-link.util';
import {
  getRegularAddressNumber,
  getRegularAddressStreet,
  getRegularCity,
  getRegularMunicipality,
  getRegularPostalCode,
  getRegularShortNameStreet,
  getRegularState,
} from './utils/regular-address-parser.util';

export const ComplianceForm: FC<{ ren1402_newFlow: boolean }> = ({
  ren1402_newFlow,
}) => {
  const purchasePolicyAdditionalData = useSelector(
    getPurchasePolicyAdditionalData,
  );
  const complianceProvider = useSelector(getVerifyPolicyComplianceProvider);
  const utmSource = useSelector(getUtmSource);
  const companyVendor = useSelector(getCompanyVendor);

  const { activeCovieLink } = complianceProvider;

  const { open, handleOpen, handleClose } = useDialog();

  const lsShowAllBuildingsAutocomplete =
    loadComplianceShowAllBuildingsFieldFromStorage();

  const [showAllBuildingsAutocomplete, setShowAllBuildingsAutocomplete] =
    useState(
      lsShowAllBuildingsAutocomplete
        ? lsShowAllBuildingsAutocomplete === 'true'
        : Boolean(!companyVendor && ren1402_newFlow),
    );

  useEffect(() => {
    saveComplianceShowAllBuildingsFieldToStorage(showAllBuildingsAutocomplete);
  }, [showAllBuildingsAutocomplete]);

  useEffect(() => {
    if (lsShowAllBuildingsAutocomplete) {
      if (companyVendor && lsShowAllBuildingsAutocomplete === 'true') {
        setPageLoading(false);
      }
      return;
    }
    if (!companyVendor && ren1402_newFlow) {
      setShowAllBuildingsAutocomplete(true);
    }
  }, [companyVendor]);

  const updateActiveCovieLink = (linkId: string | null) => {
    dispatch(
      updateComplianceProviderAction({
        activeCovieLink: linkId,
      }),
    );
  };

  const pageLoadingAnimationTimeoutRef = useRef<NodeJS.Timer | null>(null);
  const [pageLoadingAnimation, setPageLoadingAnimation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const getUrlWithQueryParams = useLocationSearch();
  const history = useNavigate();
  const dispatch = useDispatch();
  const [addressDetails, setAddressDetails] = useState<any>({});
  const verifyPolicyInput = useSelector(getVerifyPolicyInput);
  const verifyPolicyFlow = useSelector(getVerifyPolicyFlow);
  const [disabledContinueButton, setDisabledContinueButton] =
    useState<boolean>(true);
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));
  const [buildingDropdownLoading, setBuildingDropdownLoading] = useState(false);

  const {
    isSkipUnitNumber,
    handleIsSkipUnitNumberChange,
    hasBuildingIdChanged,
    updatePrevBuilding,
  } = useUnitField();

  const {
    isShowUnitAutocomplete,
    showUnitAutocompleteField,
    showRegularUnitField,
  } = useUnitFieldVisibility(COMPLIANCE_SHOW_UNIT_AUTOCOMPLETE_KEY);

  const {
    unitList,
    unitListLength,
    unitListLoading,
    updateUnitListByBuildingId,
  } = useUnitList();

  useMount(() => {
    const returnFunc = () => {
      if (pageLoadingAnimationTimeoutRef.current) {
        clearTimeout(pageLoadingAnimationTimeoutRef.current);
      }
    };

    const buildingId = getValues('buildingId');
    if (buildingId) {
      updateUnitListByBuildingId(buildingId);
    }

    dispatch(
      updateVerifyPolicyFlow({
        hasClickedComplianceContinue: false,
      }),
    );

    if (!verifyPolicyInput?.addressName) {
      return returnFunc();
    }
    setBuildingDropdownLoading(true);
    dispatch(
      getBuildingsAction.success([
        {
          id: Number(verifyPolicyInput?.buildingId),
          city: verifyPolicyInput?.address?.municipality,
          name: verifyPolicyInput?.addressName,
          state: verifyPolicyInput?.address?.state,
          street: verifyPolicyInput?.address?.shortNameStreet,
          zipCode: verifyPolicyInput?.address?.postalCode,
          streeteasy: '',
          enableManualDSDRInvitation: false,
        },
      ]),
    );
    setTimeout(() => {
      setBuildingDropdownLoading(false);
    }, 100);

    return returnFunc();
  });

  const runLoadingAnimation = useCallback(() => {
    setPageLoadingAnimation(true);
    pageLoadingAnimationTimeoutRef.current = setTimeout(() => {
      setPageLoadingAnimation(false);
    }, 500);
  }, []);

  const formHook = useForm<VerifyPolicyInput>({
    defaultValues: verifyPolicyFlow?.userFinishedComplianceFlow
      ? { unit: defaultUnit }
      : verifyPolicyInput || { unit: defaultUnit },
  });
  const { control, handleSubmit, watch, setValue, setFocus, getValues } =
    formHook;

  useEffect(() => {
    if (verifyPolicyFlow?.userFinishedComplianceFlow) {
      dispatch(resetVerifyPolicy());
    }
    if (
      verifyPolicyInput?.firstName &&
      verifyPolicyInput?.lastName &&
      verifyPolicyInput?.email &&
      verifyPolicyInput?.addressName &&
      verifyPolicyInput?.leaseStart &&
      verifyPolicyInput?.leaseEnd
    ) {
      setDisabledContinueButton(false);
    }

    updatePrevBuilding(getValues('buildingId'));

    watch((formValues) => {
      if (formValues) {
        if (hasBuildingIdChanged(formValues.buildingId)) {
          updatePrevBuilding(formValues.buildingId);
          updateUnitListByBuildingId(formValues.buildingId);
          setValue('unit', defaultUnit);
          setValue('buildingUnit', '');

          if (formValues.buildingId) {
            showUnitAutocompleteField();
          }
        }
      }

      const isFormValid =
        formValues &&
        formValues.firstName &&
        formValues.lastName &&
        formValues.email &&
        formValues.addressName &&
        formValues.leaseStart &&
        formValues.leaseEnd;

      setDisabledContinueButton(!isFormValid);

      // TODO: fix here
      dispatch(updateVerifyPolicyInput(formValues as any));
    });
  }, []);

  const onAddNewBuilding = useCallback(() => {
    setValue('unit', defaultUnit);
    setShowAllBuildingsAutocomplete(false);
    showRegularUnitField();

    setTimeout(() => {
      dispatch(
        updateVerifyPolicyInput({
          unit: defaultUnit,
          buildingUnit: '',
          buildingName: '',
          buildingId: '',
          addressName: '',
          address: addressDefaultValue,
        }),
      );
      dispatch(updateCompanyVendor(null));
      history(getUrlWithQueryParams(ROUTES.COMPLIANCE_PATH));
      void runLoadingAnimation();
    }, 100);
  }, [dispatch, getUrlWithQueryParams, history]);

  const onCompanyAddressSelection = (
    selectedAddress: ApplicationSchema['buildingAddress']['value'],
  ) => {
    if (!selectedAddress) {
      return;
    }

    setValue('buildingId', selectedAddress.id || '');
    setValue('addressName', selectedAddress.name || '');
    setValue('buildingName', selectedAddress.name || '');
    setValue('address.postalCode', selectedAddress.postalCode);
    setValue('address.state', selectedAddress.state);
    setValue('address.municipality', selectedAddress.municipality);

    let tempAddress: string[] = [];
    if (!selectedAddress.addressNumber || selectedAddress.addressNumber == '') {
      const regex = /(^[0-9]+)(\s)(.*)/i;
      tempAddress = selectedAddress.street.split(regex);
    }
    setValue('address.addressNumber', tempAddress[1] || '');
    setValue('address.street', tempAddress[3] || selectedAddress.street);
    setValue(
      'address.shortNameStreet',
      tempAddress[3] || selectedAddress.street,
    );
    setFocus('buildingUnit');
  };

  const openCovie = (linkId: any) => {
    if (!activeCovieLink) {
      updateActiveCovieLink(linkId || null);
    }
    history(getUrlWithQueryParams(ROUTES.COMPLIANCE_TERMS_PATH));
  };

  const handleCantFindAddressInCompanyList = () => {
    setValue('unit', defaultUnit);
    if (ren1402_newFlow) {
      setShowAllBuildingsAutocomplete(true);
    } else {
      showRegularUnitField();

      dispatch(
        updateVerifyPolicyInput({
          unit: defaultUnit,
          buildingUnit: '',
          buildingName: '',
          buildingId: '',
          addressName: '',
          address: addressDefaultValue,
        }),
      );
      dispatch(updateCompanyVendor(null));
    }
  };

  const onRegularAddressSelection = (address: any, addressDetails: any) => {
    const selectedAddress = addressDetails[0];
    if (selectedAddress) {
      const street = getRegularAddressStreet(
        selectedAddress.address_components,
      );
      const shortNameStreet = getRegularShortNameStreet(
        selectedAddress.address_components,
      );
      const address_number = getRegularAddressNumber(
        selectedAddress.address_components,
      );
      const municipality = getRegularMunicipality(
        selectedAddress.address_components,
      );
      const postal_code = getRegularPostalCode(
        selectedAddress.address_components,
      );
      const city = getRegularCity(selectedAddress.address_components);
      const state = getRegularState(selectedAddress.address_components);
      const cityToUse = city || municipality;
      const formattedAddress = `${address_number} ${street}, ${cityToUse}, ${state}, USA`;

      setAddressDetails({
        address_number: address_number,
        street: street.trim(),
        municipality: cityToUse,
        state: state,
        postal_code: postal_code,
        complete_address: address,
        name: address,
      });
      setValue('addressName', formattedAddress);
      setValue('address.postalCode', postal_code);
      setValue('address.state', state);
      setValue('address.municipality', cityToUse);
      setValue('address.street', street);
      setValue('address.shortNameStreet', shortNameStreet);
      setValue('address.addressNumber', address_number);
      if (address_number) {
        setFocus('buildingUnit');
      } else {
        setFocus('address.addressNumber');
      }
    } else {
      setValue('addressName', address);
    }
  };

  const { trackEvent } = useTrackEventForForm(purchasePolicyAdditionalData.id);

  const handleUnitAddClick = (value: string) => {
    if (!getValues('buildingUnit') && value) {
      setValue('buildingUnit', value);
    }

    showRegularUnitField();
  };

  const onFieldBlur = useCallback(
    (onBlur: Noop, name: string, value?: string) => () => {
      if (!value) return;
      if (onBlur) {
        onBlur();
      }
      void trackEvent(name);
    },
    [trackEvent],
  );

  const onBuildingSelected = (building: Building | null) => {
    if (!building) return;
    const formattedAddress = `${building.street}, ${building.city}, ${building.state}, USA`;

    setValue('addressName', formattedAddress);
    setValue('address.postalCode', building.zipCode);
    setValue('buildingId', building.id.toString());
    setValue('address.state', building.state);
    setValue('address.municipality', building.city);
    setValue('address.street', building.street.split(' ').slice(1).join(' '));
    setValue('address.shortNameStreet', building.street);
    setValue('address.addressNumber', building.street.split(' ')[0]);

    setFocus('buildingUnit');
  };

  const CompanyAddress = useMemo(
    () => (
      <Grid
        item
        xs={12}
        display="flex"
        flexDirection="column"
        flexWrap="nowrap"
      >
        <Controller
          control={control}
          name={'buildingId'}
          rules={{
            required: true,
          }}
          render={({ field: { onBlur, value } }) => (
            <CompanyDropdown
              value={value}
              placeholder="Your Address"
              onChange={onCompanyAddressSelection}
              setPageLoading={setPageLoading}
              onBlur={onBlur}
              handleCantFindAddressInCompanyList={
                handleCantFindAddressInCompanyList
              }
              handleCantFindAddressInCompanyListURL={
                ren1402_newFlow ? undefined : ROUTES.COMPLIANCE_PATH
              }
            />
          )}
        />
      </Grid>
    ),
    [control, onFieldBlur, ren1402_newFlow],
  );

  const RegularAddress = useMemo(
    () => (
      <>
        <Box marginBottom={2}>
          <GooglePlaceInput
            onPlaceSelect={onRegularAddressSelection}
            onPlaceError={() => {}}
            placeHolder="Street Address"
            label="Street Address"
            name="addressName"
            inputText={getValues('addressName')}
          />
        </Box>
        <Grid container spacing={2} columnSpacing={4}>
          <>
            {addressDetails.name && (
              <>
                {!addressDetails.address_number && (
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'address.addressNumber'}
                      rules={{
                        required: true,
                        maxLength: 6,
                      }}
                      render={({
                        field: { onChange, onBlur, value, name, ref },
                      }) => (
                        <TextField
                          required
                          type="number"
                          label="Address Number"
                          inputRef={ref}
                          name={name}
                          value={value}
                          onBlur={onBlur}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Grid>
                )}
              </>
            )}
            {addressDetails.name && (
              <>
                {!addressDetails.street && (
                  <Grid item xs={12} md={!addressDetails.municipality ? 6 : 12}>
                    <Controller
                      control={control}
                      name={'address.street'}
                      rules={{
                        required: true,
                        maxLength: 6,
                      }}
                      render={({
                        field: { onChange, onBlur, value, name, ref },
                      }) => (
                        <TextField
                          required
                          label="Street"
                          inputRef={ref}
                          name={name}
                          value={value}
                          onBlur={onBlur}
                          onChange={onChange}
                          inputProps={{
                            minLength: 1,
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                {!addressDetails.municipality && (
                  <Grid item xs={12} md={!addressDetails.street ? 6 : 12}>
                    <Controller
                      control={control}
                      name={'address.municipality'}
                      rules={{
                        required: true,
                      }}
                      render={({
                        field: { onChange, onBlur, value, name, ref },
                      }) => (
                        <TextField
                          required
                          label="Municipality"
                          inputRef={ref}
                          name={name}
                          value={value}
                          onBlur={onBlur}
                          onChange={onChange}
                          inputProps={{
                            minLength: 1,
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
              </>
            )}
            {addressDetails.name && (
              <>
                <Grid item xs={12} md={6}>
                  <Controller
                    control={control}
                    name={'address.state'}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, onBlur, value, name } }) => (
                      <StyledDropdown
                        id={'statedropdown'}
                        className="dropdown"
                        value={value}
                        placeholder="State"
                        isSearchable
                        onChange={onChange}
                        options={STATES}
                        name={name}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    control={control}
                    name={'address.postalCode'}
                    rules={{
                      required: true,
                      maxLength: 6,
                    }}
                    render={({
                      field: { onChange, onBlur, value, name, ref },
                    }) => (
                      <TextField
                        required
                        type={'number'}
                        label="Postal Code"
                        inputRef={ref}
                        name={name}
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        inputProps={{
                          minLength: 1,
                          maxLength: 6,
                        }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </>
        </Grid>
      </>
    ),
    [
      getValues,
      addressDetails.name,
      addressDetails.address_number,
      addressDetails.municipality,
      addressDetails.street,
      control,
    ],
  );

  const AllBuildingsInput = useMemo(
    () => (
      <Grid
        item
        xs={12}
        display="flex"
        flexDirection="column"
        flexWrap="nowrap"
      >
        <Controller
          control={control}
          name={'buildingId'}
          rules={{
            required: true,
          }}
          render={({ field: { onBlur, value, name } }) =>
            buildingDropdownLoading ? (
              <></>
            ) : (
              <BuildingsAutocompleteSelectField
                name={name}
                value={value}
                onChange={onBuildingSelected}
                onAddNewBuilding={onAddNewBuilding}
                onBlur={onBlur}
                testId="start-application-building"
                label="Select Address"
              />
            )
          }
        />
      </Grid>
    ),
    [control, onAddNewBuilding, buildingDropdownLoading],
  );

  const addressFieldsRender = useMemo(() => {
    if (showAllBuildingsAutocomplete) {
      return AllBuildingsInput;
    }
    return companyVendor ? CompanyAddress : RegularAddress;
  }, [
    showAllBuildingsAutocomplete,
    AllBuildingsInput,
    CompanyAddress,
    RegularAddress,
    companyVendor,
  ]);

  const buildingId = getValues('buildingId');
  const isCompanyVendor = !!companyVendor;

  const isUnitAutocompleteField = useMemo(() => {
    const buildingFieldTypeWithBuildingId =
      isCompanyVendor || showAllBuildingsAutocomplete;

    return (
      isShowUnitAutocomplete &&
      unitListLength &&
      (buildingFieldTypeWithBuildingId || buildingId)
    );
  }, [
    isShowUnitAutocomplete,
    isCompanyVendor,
    unitListLength,
    buildingId,
    showAllBuildingsAutocomplete,
  ]);

  const shouldDisableContinueButton = useCallback(() => {
    const formValues = getValues();
    const unitNumber = formValues.buildingUnit;
    const unitName = formValues.unit?.name;

    const unitNameExists = isUnitAutocompleteField && unitName;
    const unitNumberExists = !isUnitAutocompleteField && unitNumber;

    const isUnitMissing =
      !isSkipUnitNumber && !(unitNumberExists || unitNameExists);

    return Boolean(disabledContinueButton || isUnitMissing);
  }, [
    getValues,
    disabledContinueButton,
    isSkipUnitNumber,
    isUnitAutocompleteField,
  ]);

  const showPageLoader = useMemo(
    () => pageLoading && companyVendor,
    [companyVendor, pageLoading],
  );

  const onSubmit = async (values: VerifyPolicyInput) => {
    const isValidDates = checkDatesValidity(
      values?.leaseStart,
      values?.leaseEnd,
    );
    if (!isValidDates) {
      toast('Start date should be less than the end date', {
        type: 'error',
      });
      return;
    }

    try {
      setLoading(true);

      const isExistingUnit = isUnitAutocompleteField && !isSkipUnitNumber;

      const serializerOptions = { isExistingUnit, isSkipUnitNumber };
      const validateApplicationPolicyDto = validateApplicationPolicySerializer(
        values,
        activeCovieLink || null,
        serializerOptions,
        utmSource,
      );

      dispatch(updateVerifyPolicyFlow({ hasClickedComplianceContinue: true }));
      const updateVefifyPolicyInputData = {
        ...values,
        buildingUnit: validateApplicationPolicyDto.buildingUnit,
      };
      dispatch(updateVerifyPolicyInput(updateVefifyPolicyInputData));

      const mutation = VALIDATE_APPLICATION_POLICY(
        validateApplicationPolicyDto,
      );
      const response = await GraphQLRequest<ValidateApplicationPolicyResponse>(
        mutation,
      );

      removeComplianceShowAllBuildingsFieldFromStorage();

      const responseData = response.validateApplicationPolicy;
      const responseLinkId = getResponseComplianceLink(responseData.linkId);
      const applicationId = responseData?.application?.id;
      const validationErrors = responseData?.validation?.errors;

      if (!applicationId) {
        if (validationErrors) {
          openCovie(responseLinkId);
          return;
        }

        if ('errors' in responseData) {
          const responseErrors = responseData?.errors;

          const errorType = responseErrors[0].type;

          switch (errorType) {
            case COMPLIANCE_ERROR_TYPES.APPLICATION_ALREADY_COMPLIANT: {
              history(
                getUrlWithQueryParams(ROUTES.COMPLIANCE_ALREADY_VERIFIED_PATH),
              );
              break;
            }
            default: {
              openCovie(responseLinkId);
              break;
            }
          }
          return;
        }

        toast('Unknown response', {
          type: 'error',
        });
        return;
      }

      const routeToNavigateTo = handleApplicationStatus(
        responseData as ValidateApplicationPolicySuccessResponse['validateApplicationPolicy'],
      );

      if (routeToNavigateTo) {
        return history(routeToNavigateTo);
      }

      openCovie(responseLinkId);
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {(showPageLoader || pageLoadingAnimation) && <Loader />}
      <Grid container={true} justifyContent="center" alignItems="center">
        <Box paddingX={{ xs: 5, xl: 30 }} maxWidth={1200}>
          <Grid container rowSpacing={3} direction="column">
            <ComplianceFormSectionHeader />
            <ComplianceFormHelper />
            <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
              <Grid container rowSpacing={3} direction="column">
                <Grid item>
                  <Grid
                    container
                    flexDirection={{ xs: 'column', md: 'row' }}
                    spacing={3}
                  >
                    <Grid item xs={12} lg={4}>
                      <Typography variant="h6">
                        Tell us about yourself.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                      <Grid container spacing={2} columnSpacing={4}>
                        <Grid item xs={12} md={6}>
                          <Controller
                            control={control}
                            name={'firstName'}
                            rules={{
                              required: true,
                              maxLength: 50,
                            }}
                            render={({
                              field: { onChange, onBlur, value, name, ref },
                            }) => (
                              <TextField
                                required
                                label="First Name"
                                inputRef={ref}
                                name={name}
                                value={value}
                                onBlur={onBlur}
                                onChange={onChange}
                                inputProps={{
                                  maxLength: 50,
                                  pattern: NAME_FIELD_REGEX(50),
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Controller
                            control={control}
                            name={'lastName'}
                            rules={{
                              required: true,
                              maxLength: 50,
                            }}
                            render={({
                              field: { onChange, onBlur, value, name, ref },
                            }) => (
                              <TextField
                                required
                                label="Last Name"
                                inputRef={ref}
                                name={name}
                                value={value}
                                onBlur={onBlur}
                                onChange={onChange}
                                inputProps={{
                                  maxLength: 50,
                                  pattern: NAME_FIELD_REGEX(50),
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            control={control}
                            name={'email'}
                            rules={{
                              required: true,
                            }}
                            render={({
                              field: { onChange, onBlur, value, name, ref },
                            }) => (
                              <>
                                <TextField
                                  required
                                  type="email"
                                  label="Email"
                                  inputRef={ref}
                                  name={name}
                                  value={value}
                                  onBlur={onBlur}
                                  onChange={onChange}
                                  inputProps={{
                                    maxLength: 50,
                                  }}
                                />
                                <HelperText>
                                  Please provide the same email you used in your
                                  lease application
                                </HelperText>
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    flexDirection={{ xs: 'column', md: 'row' }}
                    spacing={3}
                  >
                    <Grid item xs={12} lg={4}>
                      <Typography variant="h6">
                        Where is the address that you are renting?
                      </Typography>
                      <ComplianceFormFieldLegend>
                        This is the address that will be used for your renters
                        insurance policy.
                      </ComplianceFormFieldLegend>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                      {addressFieldsRender}
                      <Grid item xs={12} marginTop={2}>
                        {isUnitAutocompleteField ? (
                          <UnitAutocompleteField
                            disabled={
                              !getValues('buildingId') || isSkipUnitNumber
                            }
                            control={control}
                            onFieldBlur={onFieldBlur}
                            buildingId={getValues('buildingId')}
                            options={unitList}
                            isLoading={unitListLoading}
                            onUnitAddClick={handleUnitAddClick}
                          />
                        ) : (
                          <UnitField
                            disabled={isSkipUnitNumber}
                            control={control}
                            onFieldBlur={onFieldBlur}
                          />
                        )}

                        <Box mt={1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="same_as_address"
                                onChange={(event) =>
                                  handleIsSkipUnitNumberChange(
                                    event.target.checked,
                                  )
                                }
                              />
                            }
                            label="I don't have a unit"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    flexDirection={{ xs: 'column', md: 'row' }}
                    spacing={3}
                  >
                    <Grid item xs={12} lg={4}>
                      <Typography variant="h6">
                        What is your lease start date?
                      </Typography>
                      <ComplianceFormFieldLegend>
                        What date does your contract start?
                      </ComplianceFormFieldLegend>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                      <Controller
                        control={control}
                        name={'leaseStart'}
                        rules={{
                          required: true,
                        }}
                        render={({ field: { onChange, value, name } }) => (
                          <DatePicker
                            onChange={onChange}
                            value={value}
                            name={name}
                            label={'Start Date'}
                            minDate={new Date('01/01/1950')}
                            startYear={parseInt(
                              moment
                                .default()
                                .subtract(72, 'year')
                                .format('YYYY'),
                            )}
                            endYear={parseInt(
                              moment.default().add(1, 'year').format('YYYY'),
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    flexDirection={{ xs: 'column', md: 'row' }}
                    spacing={3}
                  >
                    <Grid item xs={12} lg={4}>
                      <Typography variant="h6">
                        What is your lease end date?
                      </Typography>
                      <ComplianceFormFieldLegend variant="body1">
                        What date does your contract end?
                      </ComplianceFormFieldLegend>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                      <Controller
                        control={control}
                        name={'leaseEnd'}
                        rules={{
                          required: true,
                        }}
                        render={({ field: { onChange, value, name } }) => (
                          <DatePicker
                            onChange={onChange}
                            value={value}
                            name={name}
                            label={'End Date'}
                            minDate={new Date()}
                            startYear={parseInt(
                              moment.default().format('YYYY'),
                            )}
                            endYear={parseInt(
                              moment.default().add(10, 'year').format('YYYY'),
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    justifyContent="flex-end"
                    alignItems="center"
                    flexWrap="nowrap"
                  >
                    <Box
                      width={{
                        xs: '100%',
                        md: '75%',
                      }}
                    >
                      <ComplianceFormTermsText />
                    </Box>
                    <Grid
                      item
                      width={{
                        xs: '100%',
                        md: '25%',
                      }}
                      display="flex"
                      justifyContent="flex-end"
                      marginY={2}
                    >
                      <MuiButton
                        variant="contained"
                        type="submit"
                        disabled={shouldDisableContinueButton()}
                        fullWidth={isMd}
                        startIcon={
                          loading ? (
                            <CircularProgress color="inherit" size="17px" />
                          ) : null
                        }
                      >
                        Continue
                      </MuiButton>
                    </Grid>
                  </Grid>
                  <TermsModal
                    open={open}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    type="Compliance"
                  />
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Box>
      </Grid>
    </Container>
  );
};
